import z from "zod";

import { ENG_ARABIC_NAME_REGEX } from "@/utils/constants/arabicRegex";
import { passwordCheckers } from "@/utils/passwordCheckers";

const nameRegex = /^[A-Za-z\s'-]+$/;

export const FormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: "This field is required" })
      .max(100, { message: "Maximum length is 100 characters" })
      .regex(nameRegex, {
        message: "Оnly letters and apostrophes allowed",
      }),
    lastName: z
      .string()
      .min(1, { message: "This field is required" })
      .max(100, { message: "Maximum length is 100 characters" })
      .regex(nameRegex, {
        message: "Оnly letters and apostrophes allowed",
      }),
    phoneCode: z.string().min(1, { message: "Select a country code" }),
    phoneNumber: z.string().refine(val => /^[0-9\s\-()+]+$/.test(val), {
      message: "Only digits and common separators allowed",
      // TODO: add validation
      // Validation applies per selected country (length/format); invalid shows inline error.
    }),

    email: z
      .string()
      .min(1, { message: "This field is required" })
      .email({ message: "Enter a valid email" }),
    // TODO: add validation
    // system checks availability
    // message: "Email already in use",

    password: z
      .string()
      .refine(val => passwordCheckers.every(r => r.check(val))),

    agreement: z.boolean().optional(),
    context: z.enum(["withAgreement", "withoutAgreement"]),
  })
  .refine(
    data => {
      if (data.context === "withAgreement") {
        return data.agreement === true;
      }
      return true;
    },
    {
      message: "You should accept agreement",
      path: ["agreement"],
    }
  );

export type ProfileFormType = z.infer<typeof FormSchema>;

const phoneRegex = /^[0-9\s\-()+]+$/;

export const FormSchemaWithRole = z
  .object({
    firstName: z
      .string()
      .min(1, { message: "This field is required" })
      .max(100, { message: "Maximum length is 100 characters" })
      .regex(ENG_ARABIC_NAME_REGEX, {
        message: "Only English and Arabic letters allowed",
      }),
    lastName: z
      .string()
      .min(1, { message: "This field is required" })
      .max(100, { message: "Maximum length is 100 characters" })
      .regex(ENG_ARABIC_NAME_REGEX, {
        message: "Only English and Arabic letters allowed",
      }),
    phoneCode: z.string().min(1, { message: "Select a country code" }),
    phoneNumber: z
      .string()
      .min(1, { message: "Phone number is required." })
      .refine(val => phoneRegex.test(val), {
        message: "Only digits and common separators allowed",
      }),

    phone: z.string().optional(),
    role: z.string().optional(),
    roleId: z.string().min(1, "Role is required"),
    email: z
      .string()
      .min(1, { message: "This field is required" })
      .email({ message: "Enter a valid email" }),
  })
  .superRefine((data, ctx) => {
    if (data.phoneCode || data.phoneNumber) {
      const digitsOnly =
        (data.phoneCode?.replace(/\D/g, "") ?? "") +
        (data.phoneNumber?.replace(/\D/g, "") ?? "");
      if (digitsOnly.length > 15) {
        ctx.addIssue({
          path: ["phoneNumber"],
          code: z.ZodIssueCode.custom,
          message: "Phone number must be under 15 characters including code",
        });
      }
    }
  });

export type ProfileFormWithRole = z.infer<typeof FormSchemaWithRole>;
